import { css } from 'aphrodite';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ContactViewModel, IContact, IPhoneNumber } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { getFormattedPhoneNumber } from '../../../models/utils/phoneUtils';
import { useTelephony } from '../../contexts/telephony';
import { useErrorMessages } from '../../hooks';
import { useQueue } from '../../hooks/queue';
import {
	CellPhoneLookupConfidence,
	ICellPhoneLookupMatch,
	ICellPhoneLookupResult,
	useCellPhoneLookupMutation,
	useMarkWrongNumberMutation,
} from '../../queries/CellPhoneLookup';
import { AidaContactViewModel } from '../../viewModels/contacts';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { SmallCallButton } from '../TwilioDialer/CallButton';
import { styleSheet } from './styles';

interface IProps {
	contactId: string;
	contactName: string;
	contactViewModel: ContactViewModel;
	companyId: string;
	leadServedId: string;
	existingPhoneNumbers: IPhoneNumber[];
	onClose: () => void;
	onPhoneNumberAdded: () => void;
}

const getConfidenceStyle = (confidence: CellPhoneLookupConfidence) => {
	switch (confidence) {
		case 'High':
			return styleSheet.confidenceHigh;
		case 'MediumHigh':
			return styleSheet.confidenceMediumHigh;
		case 'Medium':
			return styleSheet.confidenceMedium;
		default:
			return styleSheet.confidenceUnresolved;
	}
};

export const CellPhoneLookupModal: React.FC<IProps> = ({
	contactId,
	contactName,
	contactViewModel,
	companyId,
	leadServedId,
	existingPhoneNumbers,
	onClose,
	onPhoneNumberAdded,
}) => {
	const queue = useQueue();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { isPendingOutcome } = useTelephony();

	const [result, setResult] = useState<ICellPhoneLookupResult | null>(null);
	const [addedNumbers, setAddedNumbers] = useState<Set<string>>(new Set());
	const [addingNumber, setAddingNumber] = useState<string | null>(null);

	const lookupMutation = useCellPhoneLookupMutation({
		onSuccess: data => {
			setResult(data);
		},
		onError: err => {
			errorMessages.pushApiError(err);
		},
	});

	const markWrongMutation = useMarkWrongNumberMutation({
		onSuccess: data => {
			setResult(data);
		},
		onError: err => {
			errorMessages.pushApiError(err);
		},
	});

	useEffect(() => {
		if (contactId) {
			setResult(null);
			setAddedNumbers(new Set());
			lookupMutation.mutate(contactId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contactId]);

	const handleMarkWrongNumber = (phoneNumber: string) => {
		markWrongMutation.mutate({ contactId, phoneNumber });
	};

	const handleAddToContact = async (match: ICellPhoneLookupMatch) => {
		setAddingNumber(match.phoneNumber);
		try {
			const newPhone: IPhoneNumber = {
				label: 'Cell',
				value: match.phoneNumber,
				metadata: { e164: match.phoneNumber },
			};

			const contactData: IContact = contactViewModel.toJs();
			const body = {
				...contactData,
				phoneNumbers: [...(existingPhoneNumbers || []), newPhone],
				company: queue.lead.company.toJs(),
				companyId: queue.lead.company.id,
			};

			const contactVm = new AidaContactViewModel(userSession, contactData);
			await contactVm.update(body);

			queue.lead.resetContacts();
			await queue.lead?.getContacts();

			setAddedNumbers(prev => new Set(prev).add(match.phoneNumber));
			toast.success('Phone number added to contact.');
			onPhoneNumberAdded();
		} catch (err) {
			errorMessages.pushApiError(err);
		} finally {
			setAddingNumber(null);
		}
	};

	const handleRetry = () => {
		setResult(null);
		lookupMutation.mutate(contactId);
	};

	const renderMatch = (match: ICellPhoneLookupMatch, index: number) => {
		const isAdded = addedNumbers.has(match.phoneNumber);
		const phoneForCall: IPhoneNumber = {
			label: 'Cell',
			value: match.phoneNumber,
			metadata: { e164: match.phoneNumber },
		};

		return (
			<div key={index} className={css(styleSheet.matchRow)}>
				<div className={css(styleSheet.matchHeader)}>
					<span className={css(styleSheet.phoneNumber)}>
						{getFormattedPhoneNumber(match.phoneNumber.replace(/^\+1/, ''))}
					</span>
					{match.isMarkedWrong ? (
						<span className={css(styleSheet.wrongNumberBadge)}>Wrong number</span>
					) : (
						<span className={css(styleSheet.confidenceBadge, getConfidenceStyle(match.confidence))}>
							{match.confidence}
						</span>
					)}
				</div>
				{!!match.matchedName && <div className={css(styleSheet.matchedName)}>Matched: {match.matchedName}</div>}
				<div className={css(styleSheet.metaRow)}>
					{!!match.phoneType && <span className={css(styleSheet.metaLabel)}>{match.phoneType}</span>}
					{match.isConnected === false && <span className={css(styleSheet.disconnectedBadge)}>Disconnected</span>}
				</div>
				{match.confidenceReasons?.length > 0 && (
					<div className={css(styleSheet.reasons)}>{match.confidenceReasons.join(', ')}</div>
				)}
				{!!match.notes && <div className={css(styleSheet.notes)}>{match.notes}</div>}
				<div className={css(styleSheet.actions)}>
					{isAdded ? (
						<span className={css(styleSheet.addedBadge)}>Added</span>
					) : match.isMarkedWrong ? null : (
						<>
							<SmallCallButton
								phoneNumber={phoneForCall}
								companyId={companyId}
								contactId={contactId}
								leadServedId={leadServedId}
								isDisabled={isPendingOutcome}
							/>
							<button
								className={css(styleSheet.markWrongButton)}
								disabled={markWrongMutation.isPending}
								onClick={() => handleMarkWrongNumber(match.phoneNumber)}
							>
								Mark wrong number
							</button>
							<button
								className={css(styleSheet.actionButton)}
								disabled={addingNumber === match.phoneNumber}
								onClick={() => handleAddToContact(match)}
							>
								{addingNumber === match.phoneNumber ? 'Adding...' : 'Add to contact'}
							</button>
						</>
					)}
				</div>
			</div>
		);
	};

	const renderContent = () => {
		if (lookupMutation.isPending) {
			return (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='small' />
					<span className={css(styleSheet.loadingText)}>Searching for cell phone numbers...</span>
				</div>
			);
		}

		if (lookupMutation.isError && !result) {
			return (
				<div className={css(styleSheet.errorContainer)}>
					<span className={css(styleSheet.errorText)}>Failed to look up cell phone numbers.</span>
					<button className={css(styleSheet.retryButton)} onClick={handleRetry}>
						Retry
					</button>
				</div>
			);
		}

		if (!result) {
			return null;
		}

		if (result.status === 'NoResults' || result.status === 'Failed' || !result.matches?.length) {
			return <div className={css(styleSheet.noResultsText)}>No cell phone numbers found for this contact.</div>;
		}

		return <div>{result.matches.filter(m => !!m.phoneNumber).map(renderMatch)}</div>;
	};

	return (
		<div
			className={css(styleSheet.modal)}
			role='dialog'
			aria-modal='false'
			aria-labelledby='cell-phone-lookup-title'
			onKeyDown={e => e.key === 'Escape' && onClose()}
		>
			<div className={css(styleSheet.header)}>
				<span id='cell-phone-lookup-title' className={css(styleSheet.title)}>
					Cell Phone Lookup: {contactName}
				</span>
				<button className={css(styleSheet.closeButton)} onClick={onClose}>
					&times;
				</button>
			</div>
			{renderContent()}
		</div>
	);
};
